import { FC, FormEvent, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import agentsService from '@/services/agents.service';
import quotaService from '@/services/quota.service';
import { MyQuota } from '@/types/agents';
import { useToast } from '@/contexts/ToastContext';
import LoadingSpinner from '@/components/LoadingSpinner';
import EmojiAvatarPicker from '@/components/EmojiAvatarPicker';
import { isQuotaError, quotaErrorMessage } from '@/utils/quotaErrorToast';

const DEFAULT_PROMPT =
  'Você é um assistente útil. Responda em português, de forma clara e objetiva, ' +
  'citando as fontes dos documentos quando possível.';

const AgentCreatePage: FC = () => {
  const navigate = useNavigate();
  const { showToast } = useToast();
  const [quota, setQuota] = useState<MyQuota | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    name: '',
    description: '',
    avatar: '🤖',
    system_prompt: DEFAULT_PROMPT,
    model: '',
    temperature: 0.3,
  });

  useEffect(() => {
    quotaService
      .getMyQuota()
      .then((q) => {
        setQuota(q);
        setForm((f) => ({ ...f, model: q.allowed_models[0] || 'gpt-5.4-nano' }));
      })
      .catch(() => showToast('Não foi possível carregar sua cota.', 'error'));
  }, [showToast]);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const agent = await agentsService.create(form);
      showToast('Agente criado! Agora suba arquivos de conhecimento.', 'success');
      navigate(`/agentes/${agent.id}/configurar`);
    } catch (err) {
      if (isQuotaError(err)) {
        showToast(quotaErrorMessage(err.response.data), 'error');
      } else {
        showToast('Não foi possível criar o agente.', 'error');
      }
    } finally {
      setSubmitting(false);
    }
  };

  if (!quota) {
    return (
      <div className="flex justify-center py-16">
        <LoadingSpinner size="large" />
      </div>
    );
  }

  return (
    <form
      onSubmit={handleSubmit}
      data-testid="agent-create-form"
      className="max-w-2xl mx-auto p-6 md:p-10 space-y-5"
    >
      <h1 className="text-2xl font-bold text-content-primary">Novo agente pessoal</h1>

      <div className="flex items-start gap-4">
        <EmojiAvatarPicker value={form.avatar} onChange={(v) => setForm((f) => ({ ...f, avatar: v }))} />
        <label className="flex-1 block text-sm text-content-secondary">
          Nome
          <input
            type="text"
            required
            maxLength={100}
            data-testid="agent-name"
            className="mt-1 w-full rounded-lg border border-surface-border bg-surface-secondary p-2 text-content-primary"
            value={form.name}
            onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
          />
        </label>
      </div>

      <label className="block text-sm text-content-secondary">
        Descrição (opcional)
        <input
          type="text"
          maxLength={500}
          data-testid="agent-description"
          className="mt-1 w-full rounded-lg border border-surface-border bg-surface-secondary p-2 text-content-primary"
          value={form.description}
          onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
        />
      </label>

      <label className="block text-sm text-content-secondary">
        Personalidade / instruções
        <textarea
          required
          rows={6}
          data-testid="agent-system-prompt"
          className="mt-1 w-full rounded-lg border border-surface-border bg-surface-secondary p-2 text-content-primary font-mono text-sm"
          value={form.system_prompt}
          onChange={(e) => setForm((f) => ({ ...f, system_prompt: e.target.value }))}
        />
      </label>

      <label className="block text-sm text-content-secondary">
        Modelo de IA
        <select
          data-testid="agent-model"
          className="mt-1 w-full rounded-lg border border-surface-border bg-surface-secondary p-2 text-content-primary"
          value={form.model}
          onChange={(e) => setForm((f) => ({ ...f, model: e.target.value }))}
        >
          {quota.allowed_models.map((m) => (
            <option key={m} value={m}>
              {quota.available_models_catalog[m]?.label || m} ({m})
            </option>
          ))}
        </select>
      </label>

      <label className="block text-sm text-content-secondary">
        Criatividade ({form.temperature.toFixed(1)})
        <input
          type="range"
          min={0}
          max={1}
          step={0.1}
          data-testid="agent-temperature"
          className="mt-1 w-full accent-accent"
          value={form.temperature}
          onChange={(e) => setForm((f) => ({ ...f, temperature: parseFloat(e.target.value) }))}
        />
        <span className="text-xs text-content-tertiary">0 = preciso · 1 = criativo</span>
      </label>

      <div className="rounded-lg bg-surface-secondary border border-surface-border p-3 text-sm text-content-secondary">
        Limite de agentes pessoais: <strong className="text-content-primary">{quota.max_personal_agents}</strong>.
        Arquivos por agente: <strong className="text-content-primary">{quota.max_files_per_agent}</strong> (até{' '}
        {quota.max_file_size_mb}MB cada).
      </div>

      <div className="flex gap-2 justify-end">
        <button
          type="button"
          onClick={() => navigate('/agentes')}
          className="px-4 py-2 rounded-lg text-content-secondary hover:text-content-primary"
        >
          Cancelar
        </button>
        <button
          type="submit"
          disabled={submitting}
          data-testid="agent-create-submit"
          className="bg-accent text-surface-primary font-semibold px-4 py-2 rounded-lg hover:opacity-90 transition disabled:opacity-50"
        >
          {submitting ? 'Criando...' : 'Criar agente'}
        </button>
      </div>
    </form>
  );
};

export default AgentCreatePage;
